import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Skeleton,
} from '@mui/material';

import { useGetTemplates } from '@frontend/features/whatsapp/hooks/useGetTemplates';
import { useUpdateTemplateEnabled } from '@frontend/features/whatsapp/hooks/useUpdateTemplateEnabled';

type WhatsappTemplatesSectionProps = {
  agencyId: number;
};

export const WhatsappTemplatesSection = ({ agencyId }: WhatsappTemplatesSectionProps) => {
  const { data, isLoading } = useGetTemplates(agencyId);
  const updateTemplateEnabled = useUpdateTemplateEnabled(agencyId);
  const [searchText, setSearchText] = useState('');

  const templates = data?.templates || [];
  const filteredTemplates = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return templates;
    return templates.filter((template) => {
      const name = template.elementName || '';
      const id = template.id || '';
      return `${name} ${id}`.toLowerCase().includes(searchValue);
    });
  }, [templates, searchText]);

  return (
    <Box maxWidth={900} display="flex" flexDirection="column" gap={2}>
      <Typography variant="h6">Templates</Typography>
      <TextField
        label="Search templates"
        placeholder="Search templates"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        fullWidth
        size="small"
      />
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 2 }} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Template ID</TableCell>
              <TableCell>Template Name</TableCell>
              <TableCell align="center">Enabled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>{template.id}</TableCell>
                <TableCell>{template.elementName || template.id}</TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={template.enabled !== false}
                    onChange={(event) =>
                      updateTemplateEnabled.mutate({
                        templateId: template.id,
                        enabled: event.target.checked,
                      })
                    }
                    disabled={updateTemplateEnabled.isLoading}
                  />
                </TableCell>
              </TableRow>
            ))}
            {filteredTemplates.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No templates found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
